import React, { Component, createRef } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { IFinalFormFieldInputComponentProps } from '@framework/ui/Organisms/FinalForm/interfaces';
import FormError from '@dealroadshow/uikit-form/FormError';
import FormField from '@dealroadshow/uikit-form/FormField';
import Label from '@dealroadshow/uikit-input/Label';
import InputAppend from '@dealroadshow/uikit-input/InputAppend';
import InputPrepend from '@dealroadshow/uikit-input/InputPrepend';
import { IconComponentType } from '@dealroadshow/uikit-icon';
import { inputStyles as styles } from '@dealroadshow/uikit-input';

interface IProps extends IFinalFormFieldInputComponentProps {
  name: string,
  type?: 'number' | 'text' | 'date' | 'time' | 'email' | 'password' | 'hidden',
  label?: React.ReactNode,
  placeholder?: string,
  value?: string | number,
  className?: string,
  inputClassName?: string,
  labelClassName?: string,
  iconPosition?: 'left' | 'right',
  icon?: IconComponentType,
  size?: 'small' | 'medium',
  minLength?: number,
  maxLength?: number,
  min?: number,
  max?: number,
  iconClassName?: string,
  disabled?: boolean,
  isReadOnly?: boolean,
  isNarrow?: boolean,
  isErrorMessage?: boolean,
  isClearable?: boolean,
  autoFocus?: boolean,
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onClick?: React.MouseEventHandler,
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onKeyPress?: React.KeyboardEventHandler,
  prependComponent?: React.ReactElement | React.ElementType | React.FC<any>,
  appendComponent?: React.ReactElement | React.ElementType | React.FC<any>,
  isLastPassAutofillEnabled?: boolean,
  formFieldClassName?: string,
  dataTest: string,
  autoComplete?: string,
}

interface IState {
  isFocused: boolean,
  isHovered: boolean,
}

const defaultProps: Partial<IProps> = {
  type: 'text',
  iconPosition: 'left',
  disabled: false,
  isReadOnly: false,
  isNarrow: false,
  isErrorMessage: true,
  isClearable: true,
  autoFocus: false,
  value: '',
  size: 'medium',
  isLastPassAutofillEnabled: false,
  onBlur: noop,
  onClick: noop,
  onChange: noop,
  onFocus: noop,
  onKeyPress: noop,
};

/**
 * @deprecated This component is DEPRECATED. Use Input from @dealroadshow/uikit-input.
 */
class Input extends Component<IProps, IState> {
  static defaultProps: Partial<IProps> = defaultProps;

  // eslint-disable-next-line react/sort-comp
  private nodeInput: any;

  constructor(props: IProps) {
    super(props);

    this.state = {
      isFocused: false,
      isHovered: false,
    };

    this.handleOnChange = this.handleOnChange.bind(this);
    this.value = this.value.bind(this);

    this.nodeInput = createRef();
  }

  /**
   * Can be called by ref.focus()
   */
  focus = (event?: React.FocusEvent<HTMLInputElement>) => {
    this.setState({
      isFocused: true,
    });

    if (this.props.input && ('onFocus' in this.props.input)) {
      this.props.input.onFocus(event);
    }
    if (this.nodeInput.current) {
      this.nodeInput.current.focus();
    }

    // @ts-ignore
    return this.props.onFocus(event);
  };

  onBlur = (event?: React.FocusEvent<HTMLInputElement>) => {
    if (!this.state.isHovered) {
      this.setState({
        isFocused: false,
      });
    }

    if (this.props.input && ('onBlur' in this.props.input)) {
      this.props.input.onBlur(event);
    }

    // @ts-ignore
    return this.props.onBlur(event);
  };

  value() {
    if (this.props.input && ('value' in this.props.input)) {
      return this.props.input.value;
    }
    return this.props.value;
  }

  handleHoverOn = () => {
    this.setState({ isHovered: true });
  };

  handleHoverOff = () => {
    this.setState({ isHovered: false });
  };

  handleOnChange = (event: React.ChangeEvent<HTMLInputElement> | string) => {
    if (this.props.input && ('onChange' in this.props.input)) {
      this.props.input.onChange(event);

      /**
       * Autofill writes into unfocused fields without focus/blur
       * so mark the field touched to show its errors
       * @see https://finsight.myjetbrains.com/youtrack/issue/FIN-34588
       */
      if (event instanceof Object && event.target !== document.activeElement) {
        this.props.input.onBlur?.();
      }
    }
    // @ts-ignore
    return this.props.onChange({
      // @ts-ignore
      target: {
        value: ((event instanceof Object) ? event.target.value : event),
      },
    });
  };

  clear = () => {
    this.handleOnChange('');
  };

  renderPrependComponent = () => {
    const {
      prependComponent,
      onClick,
      icon,
      iconPosition,
      iconClassName,
      size,
    } = this.props;
    const isCustomIconVisible = Boolean(((iconPosition === 'left') && icon));

    if (prependComponent || isCustomIconVisible) {
      return (
        <InputPrepend
          onClick={ onClick }
          prependComponent={ prependComponent }
          icon={ icon }
          isCustomIconVisible={ isCustomIconVisible }
          iconClassName={ iconClassName }
          size={ size }
        />
      );
    }

    return null;
  };

  renderAppendComponent = () => {
    const {
      appendComponent,
      onClick,
      isClearable,
      icon,
      iconPosition,
      iconClassName,
      size,
      disabled,
    } = this.props;

    const isCustomIconVisible = Boolean(((iconPosition === 'right') && icon));

    if (appendComponent || isCustomIconVisible || isClearable) {
      const isClearButtonVisible =
        !disabled && isClearable && (this.state.isHovered || this.state.isFocused) && this.value().length > 0;

      return (
        <InputAppend
          clear={ this.clear }
          onClick={ onClick }
          // @ts-ignore
          appendComponent={ appendComponent }
          icon={ icon }
          isCustomIconVisible={ isCustomIconVisible }
          iconClassName={ iconClassName }
          size={ size }
          // @ts-ignore
          isClearIconVisible={ isClearButtonVisible }
        />
      );
    }

    return null;
  };

  /**
   * @return {ReactElement}
   */
  render() {
    let {
      input,
      meta,
      type,
      name,
      label,
      placeholder,
      className,
      inputClassName,
      labelClassName,
      icon,
      prependComponent: PrependComponent,
      appendComponent: AppendComponent,
      iconPosition,
      isNarrow,
      isErrorMessage,
      isClearable,
      disabled,
      isReadOnly,
      autoFocus,
      maxLength,
      minLength,
      max,
      min,
      size,
      isLastPassAutofillEnabled,
      onClick,
      onBlur,
      onKeyPress,
      formFieldClassName,
      dataTest,
      autoComplete,
    } = this.props;
    const {
      isHovered,
      isFocused,
    } = this.state;
    const value = this.value();
    const isClearIconVisible = isClearable && value?.toString().length > 0 && !disabled && (isHovered || isFocused);

    const inputWrapperCls = cn(styles.formInputWrp, {
      [styles.isPrependComponent]: PrependComponent || ((iconPosition === 'left') && icon),
      [styles.isAppendComponent]: AppendComponent || ((iconPosition === 'right') && icon) || isClearIconVisible,
      [styles.isInputIconDisabled]: icon && disabled,
    }, className);

    const inputCls = cn(styles.formInput, inputClassName, {
      [styles.isError]: (meta && meta.touched && meta.error),
      // @ts-ignore
      [styles.isWarning]: meta && meta.touched && meta.warning,
      [styles.isClearable]: isClearIconVisible,
      [styles.small]: size === 'small',
    });

    const inputProps = {
      onClick,
      // @ts-ignore
      onBlur, // is overrided by onBlur passed in input
      onKeyPress,
      autoComplete,
      ...input,
    };
    return (
      <FormField
        isNarrow={ isNarrow }
        isValidationFeedback={
          // @ts-ignore
          !!(meta && meta.touched && (meta.error || meta.warning))
        }
        dataTest={ dataTest }
        className={ formFieldClassName }
      >
        { label && (
          <Label htmlFor={ input.name } className={ labelClassName }>
            { label }
          </Label>
        ) }
        <label
          className={ inputWrapperCls }
          onMouseOver={ this.handleHoverOn }
          onMouseLeave={ this.handleHoverOff }
        >
          { this.renderPrependComponent() }
          <input
            key="input"
            // @ts-ignore
            name={ name }
            type={ type }
            disabled={ disabled }
            placeholder={ placeholder }
            data-lpignore={ !isLastPassAutofillEnabled }
            autoFocus={ autoFocus }
            maxLength={ maxLength }
            minLength={ minLength }
            max={ max }
            min={ min }
            readOnly={ isReadOnly }
            // @ts-ignore
            value={ this.value() }
            { ...inputProps }
            className={ inputCls }
            onChange={ this.handleOnChange }
            onFocus={ this.focus }
            onBlur={ this.onBlur }
            ref={ this.nodeInput }
            autoComplete={ autoComplete }
          />
          { this.renderAppendComponent() }
          { isErrorMessage && <FormError { ...meta } /> }
        </label>
      </FormField>
    );
  }
}

export default Input;
